/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TextField, IconButton, CircularProgress, Box, InputAdornment } from '@mui/material';

import { UserService } from '../../../Services/UserService';

import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import { Patterns } from '../../../../../Shared/Constants';
import { FIELD_TYPES, UserState } from '../../../Models/Company.Model';

import { Icons } from '../../../../../Shared/Constants/Icons';

import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import { LabelText, NameAndLabel, UserDetailsContainer, ValueText } from './UserDetails.Style';
import { buttonStyles, ErrorMsg } from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import { SnackbarSeverity, timeoutValue } from '../../../../../Shared/Constants/App';

const EditUserDetails = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location?.state as UserState | undefined;
  const [user, setUser] = useState({ ...state?.rowInfo });
  const { t } = useTranslation(['addUsers']);

  const [isEditing, setIsEditing] = useState(false);
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [newUsername, setNewUsername] = useState(user?.username || '');
  const [isSaving, setIsSaving] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState(SnackbarSeverity.success);

  const { textField } = buttonStyles;

  const handleEdit = () => setIsEditing(true);

  const handleClearField = (type: keyof typeof FIELD_TYPES) => {
    if (type === FIELD_TYPES.email) {
      setNewEmail(user?.email || '');
      setEmailError('');
    } else if (type === FIELD_TYPES.username) {
      setNewUsername(user?.username || '');
      setUsernameError('');
    }
  };

  const handleCancel = () => {
    setNewEmail(user?.email || '');
    setNewUsername(user?.username || '');
    setEmailError('');
    setUsernameError('');
    setIsEditing(false);
  };

  const validateFields = () => {
    let valid = true;
    setUsernameError('');
    setEmailError('');

    if (!newUsername.trim()) {
      setUsernameError(t('addUsers:nameReq'));
      valid = false;
    } else if (!Patterns?.AlphaNumericWithSpaces?.test(newUsername)) {
      setUsernameError(t('addUsers:invalidName'));
      valid = false;
    }

    if (newEmail && !Patterns?.Email?.test(newEmail)) {
      setEmailError(t('addUsers:invalidEmailProvided'));
      valid = false;
    }

    return valid;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    try {
      if (!user?.organizationId || !user?.userId) return;
      setIsSaving(true);

      const updatePayload = {
        organizationId: user?.organizationId,
        userId: user?.userId,
        email: newEmail,
        username: newUsername,
      };

      const response = await UserService.updateUserDetails(updatePayload);

      if (!response.errors?.length) {
        setUser({ ...user, email: newEmail, username: newUsername });
        setIsEditing(false);
        setSnackbarMessage(t('addUsers:userUpdatedSuccessfully'));
        setSnackbarSeverity(SnackbarSeverity.success);
        setSnackbarOpen(true);
        setTimeout(() => navigate('/company'), timeoutValue);
      } else {
        console.error('Error updating user:', response.errors);
        setSnackbarMessage(t('addUsers:failedToUpdateUser'));
        setSnackbarSeverity(SnackbarSeverity.error);
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setSnackbarMessage(t('addUsers:failedToUpdateUser'));
      setSnackbarSeverity(SnackbarSeverity.error);
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const role = user?.roles?.[0]?.roleName ?? '-';

  const fields = [
    { key: 'username', label: `${t('addUsers:name')}`, value: user?.username },
    { key: 'email', label: `${t('addUsers:emailAddress')}`, value: user?.email || '-' },
    { label: `${t('jobManagement:employeeNumber')}`, value: user?.intId || '-' },
    { label: `${t('addUsers:role')}`, value: role },
    { label: `${t('addUsers:phoneNumber')}`, value: user?.phoneNumber || '-' },
  ];

  const renderFieldContent = (field: any) => {
    const isEditableField = field?.key === FIELD_TYPES.email || field.key === FIELD_TYPES.username;

    if (!isEditableField) {
      return <ValueText>{field?.value}</ValueText>;
    }

    if (!isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ValueText>{field?.value}</ValueText>
          <IconButton onClick={handleEdit}>
            <img src={Icons.EditBlackIcon} alt={t('altTexts:edit')} />
          </IconButton>
        </Box>
      );
    }

    const value = field?.key === FIELD_TYPES.email ? newEmail : newUsername;
    const error =
      field.key === FIELD_TYPES.email ? emailError : field.key === FIELD_TYPES.username ? usernameError : '';

    return (
      <>
        <TextField
          sx={textField}
          value={value}
          onChange={(e) => handleFieldChange(field?.key, e?.target?.value)}
          disabled={isSaving}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    handleClearField(field?.key === FIELD_TYPES.email ? FIELD_TYPES.email : FIELD_TYPES.username)
                  }
                  disabled={isSaving}>
                  <img src={Icons.CancelIcon} alt={t('altTexts:clear')} width={24} height={24} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {error && <ErrorMsg>{error}</ErrorMsg>}
      </>
    );
  };

  const handleFieldChange = (key: string, value: string) => {
    if (key === FIELD_TYPES.email) {
      setNewEmail(value);
      if (value && !Patterns?.Email?.test(value)) {
        setEmailError(t('addUsers:invalidEmailProvided'));
      } else {
        setEmailError('');
      }
    } else if (key === FIELD_TYPES.username) {
      setNewUsername(value);
      if (!value.trim()) {
        setUsernameError(t('addUsers:nameReq'));
      } else if (!Patterns.AlphaNumericWithSpaces.test(value)) {
        setUsernameError(t('addUsers:invalidName'));
      } else {
        setUsernameError('');
      }
    }
  };

  return (
    <>
      <PageTitle title={t('addUsers:modifyUserDetails')} />
      <CustomerContainer>
        <UserDetailsContainer>
          {fields?.map((field, index) => (
            <NameAndLabel key={index}>
              <LabelText>{field?.label}</LabelText>
              {renderFieldContent(field)}
            </NameAndLabel>
          ))}
          {isEditing && (
            <Box sx={{ display: 'flex', gap: '8px', mt: 2, justifyContent: 'flex-end' }}>
              <Button
                type={ButtonType.Primary}
                label={
                  isSaving ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={24} color="inherit" />
                    </Box>
                  ) : (
                    t('homePage:modify')
                  )
                }
                onClick={handleSave}
                disabled={isSaving || !!emailError || !!usernameError}
              />
              <Button
                type={ButtonType.Secondary}
                label={t('dashboard:cancel')}
                onClick={handleCancel}
                disabled={isSaving}
              />
            </Box>
          )}
        </UserDetailsContainer>
        <SnackbarMessage
          open={snackbarOpen}
          successMessage={snackbarMessage}
          errorMessage={snackbarMessage}
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
        />
      </CustomerContainer>
    </>
  );
};

export default EditUserDetails;
